// Thin fetch wrapper around the backend API.
//
// Centralizes the two things every call used to repeat by hand: the base URL
// and the `Authorization: Bearer <token>` header (read from localStorage, which
// the auth provider keeps in sync). Callers only deal with paths and bodies and
// receive a uniform { ok, status, data } result.

// Defaults to '.' so requests stay relative to the page (e.g. './api/login'),
// matching the original behaviour. Override with REACT_APP_API_BASE_URL.
const BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '.'

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token')
  return token ? { Authorization: 'Bearer ' + token } : {}
}

export interface ApiRequestOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
  auth?: boolean
}

// A failed request's body is never guaranteed to match T (it might be an error
// object, plain text like "Token invalid", or unparseable) — so `data` is only
// typed as T on the `ok: true` branch. This is a discriminated union on
// purpose: it forces every call site to check `ok` before trusting the shape
// of `data`, which `tsc` then enforces at compile time.
export type ApiResult<T> = { ok: true; status: number; data: T } | { ok: false; status: number; data: unknown }

export async function apiRequest<T = unknown>(
  path: string,
  { method = 'GET', body, headers = {}, auth = true }: ApiRequestOptions = {}
): Promise<ApiResult<T>> {
  const requestHeaders: Record<string, string> = { ...(auth ? authHeaders() : {}), ...headers }
  const init: RequestInit = { method, headers: requestHeaders }

  if (body !== undefined) {
    requestHeaders['Content-Type'] = 'application/json'
    init.body = JSON.stringify(body)
  }

  const response = await fetch(BASE_URL + path, init)

  // Most endpoints return JSON, a few return plain text — fall back gracefully.
  const raw = await response.text()
  let data: unknown = raw
  try {
    data = JSON.parse(raw)
  } catch {
    // not JSON — keep the raw text
  }

  if (!response.ok) {
    return { ok: false, status: response.status, data }
  }
  return { ok: true, status: response.status, data: data as T }
}
